import { Illustration, Text, Title } from "@clipboard-health/ui-components";
import { toErrorMessage } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { PageWrapper } from "../../components/PageWrapper";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { OnboardingPageHeader, type OnboardingPageHeaderProps } from "../SharedPageHeader";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";

interface OnboardingPersonalIdErrorProps extends Pick<OnboardingPageHeaderProps, "progress"> {
  isLoading: boolean;
  onTryAgain: () => Promise<void>;
  onBack: () => void;
  analytics: OnboardingPersonalIdVerificationAnalytics;
  qualificationsError: unknown | undefined;
  workerRequirementsError: unknown | undefined;
}

export function OnboardingPersonalIdError({
  isLoading,
  onTryAgain,
  analytics,
  qualificationsError,
  workerRequirementsError,
  progress,
  onBack,
}: OnboardingPersonalIdErrorProps) {
  useLogErrorEffect(APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_FETCH_ERROR, {
    error: new Error("Error fetching personal ID requirement"),
    metadata: {
      ...analytics,
      qualificationsErrorMessage: toErrorMessage(qualificationsError),
      workerRequirementsErrorMessage: toErrorMessage(workerRequirementsError),
    },
  });

  return (
    <PageWrapper variant="tertiary">
      <OnboardingPageHeader progress={progress} onClickBack={onBack} />
      <PageContent>
        <Stack
          gap={4}
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          flex={1}
          padding={4}
        >
          <Illustration type="error" sx={{ alignSelf: "center" }} />
          <Title variant="h3" component="h1">
            Something went wrong
          </Title>
          <Text variant="body1">
            There was an error fetching details for your personal ID verification
          </Text>
        </Stack>
      </PageContent>
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          isLoading={isLoading}
          onClick={async () => {
            await onTryAgain();
          }}
        >
          Try Again
        </LoadingButton>
      </PageFooterWrapper>
    </PageWrapper>
  );
}
